<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <autocomplete
          v-model="selectedCompany"
          :label="$t('select_company')"
          :items="companies"
          item-text="name"
          item-value="id"
          @change="loadUsers"
          return-object
          :loading="loadingCompanies"
        />
      </v-col>
      <v-col cols="12">
        <v-row>
          <v-col cols="7">
            <v-data-table
              v-model="selectedUsers"
              :headers="headers"
              :items="users"
              class="ordera-box-shadow striped-table"
              item-key="id"
              show-select
              dense
              hide-default-footer
              disable-pagination
              :loading="loading"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>
                    {{ $t("marked") }} {{ selectedUsers.length }}
                    {{ $t("users_lowercase") }}
                  </v-toolbar-title>
                  <v-divider class="mx-4" inset vertical></v-divider>
                  <v-spacer></v-spacer>
                  <primary-button
                    @click="sendEmail"
                    :disabled="sending"
                    :loading="sending"
                    :dark="sending"
                  >
                    {{ $t("send_email_btn") }}
                  </primary-button>
                </v-toolbar>
              </template>
            </v-data-table>
          </v-col>
          <v-col cols="5">
            <text-field :label="$t('subject')" v-model="subject"></text-field>
            <vue-editor v-model="content" :editor-toolbar="toolbarOptions"></vue-editor>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { VueEditor } from "vue2-editor";
import hasSnackbar from "@/mixins/hasSnackbar";
import companyService from "@/services/company";
import emailService from "@/services/email";

export default {
  name: "SendEmail",
  mixins: [hasSnackbar],
  components: {
    VueEditor,
  },
  data() {
    return {
      users: [],
      companies: [],
      selectedCompany: null,
      selectedUsers: [],
      content: "",
      subject: "",
      toolbarOptions: [
        ["bold", "italic", "underline", "strike"],
        [{ header: 1 }, { header: 2 }],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        ["clean"],
      ],
      loading: false,
      sending: false,
      loadingCompanies: false
    };
  },
  created() {
    this.loadCompanies();
  },
  methods: {
    loadUsers() {
      this.loading = true;
      this.selectedUsers = [];
      companyService
        .getCompanyUsers(this.selectedCompany.id)
        .then((response) => {
          this.users = response.data.users.map((user, index) => {
            return {
              id: user.id || `user-${index}`,
              name: user.name,
              email: user.private_email || "",
              state: "-",
            };
          });
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_users"));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadCompanies() {
      this.loadingCompanies = true;
      companyService.getCompanies().then((response) => {
        this.companies = response.data;
      })
      .catch(() => {
        this.showErrorSnackbar(this.$t("error_loading_companies"));
      })
      .finally(() => {
        this.loadingCompanies = false;
      });
    },
    chunk(arr, size) {
      const chunks = [];
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
      }
      return chunks;
    },

    async sendEmail() {
      if (this.selectedUsers.length === 0) {
        this.showErrorSnackbar(this.$t("please_select_users_for_email"));
        return;
      }

      this.sending = true;

      try {
        const userBatches = this.chunk(this.selectedUsers, 5);
        let successCount = 0;
        let errorCount = 0;

        for (let i = 0; i < userBatches.length; i++) {
          const batch = userBatches[i];
          const payload = {
            recipients: batch,
            subject: this.subject,
            content: this.content,
            company_id: this.selectedCompany.id,
          };

          try {
            const response = await emailService.sendEmailBatch(payload);

            response.data.info.forEach((user) => {
              let userInList = this.selectedUsers.find(
                (userInList) => userInList.email === user.email
              );
              if (userInList) {
                userInList.state = user.status;
                if (user.status === 'sent' || user.status === 'success') {
                  successCount++;
                } else {
                  errorCount++;
                }
              }
            });

            await new Promise((resolve) => setTimeout(resolve, 500));
          } catch (error) {
            console.error(`${this.$t("error_sending_group")} ${i + 1}:`, error);

            batch.forEach((user) => {
              user.state = this.$t("error_sending");
              errorCount++;
            });
          }
        }

        if (errorCount === 0) {
          this.showSuccessSnackbar(this.$t("all_emails_sent_successfully"));
        } else if (successCount > 0) {
          this.showSuccessSnackbar(this.$t("emails_sent_with_errors", { success: successCount, errors: errorCount }));
        } else {
          this.showErrorSnackbar(this.$t("error_sending_emails"));
        }
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_sending_emails"));
      } finally {
        this.sending = false;
      }
    },
  },
  computed: {
    headers() {
      return [
        { text: this.$t("name"), value: "name" },
        { text: this.$t("email"), value: "email" },
        { text: this.$t("status"), value: "state" },
      ];
    },
  },
};
</script>
